import React, { useState, useMemo, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useTheme, alpha, useMediaQuery } from '@mui/material'
import AppBoxComponent from 'components/mui/AppBoxComponent'
import AppTypographyComponent from 'components/mui/AppTypographyComponent'
import AppButtonComponent from 'components/mui/AppButtonComponent'
import { CalendarMonth, KeyboardArrowRight } from '@mui/icons-material'
import { typography } from 'theme'
import CustomTabs from 'components/common/tabs/CustomTabs'
import DateRangePicker from 'components/common/DateRangePicker'
import NodisplayComponent from 'components/common/NodisplayComponent'

const OrdersView = ({
  mockOrdersData = [],
  handleOrderClick = () => { },
  pagination = null,
  onPageChange = () => { },
  onDateRangeChange = () => { },
  onOrderTypeChange = () => { },
  showTabs = true,
  leftControl = null,
  showDateRange = true
}) => {
  // States
  const theme = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const urlParams = new URLSearchParams(location.search)
  const initialType = urlParams.get('type')
  const initialTypeIndex = initialType === 'sip' ? 0 : (initialType === 'lumpsum' ? 1 : (initialType === 'redeem' ? 2 : (initialType === 'switch' ? 3 : 0)))

  const [selectedType, setSelectedType] = useState(initialTypeIndex)
  const [dateRangeValue, setDateRangeValue] = useState('')
  const [hasDateRange, setHasDateRange] = useState(false) // Track if date range is selected

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'executed':
        return '#10B981'
      case 'pending':
      case 'processing':
      case 'in progress':
        return '#F1C40F' // Vibrant Yellow for In Progress
      case 'failed':
        return '#EF4444'
      case 'cancelled':
        return '#EF4444' // Match failed/error color for cancelled
      default:
        return '#9CA3AF'
    }
  }

  const getStatusText = (status) => {
    if (!status) return 'Pending'
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'SIP':
        return '#3B82F6'
      case 'Lumpsum':
        return '#8B5CF6'
      case 'Redemption':
        return '#EF4444'
      case 'STP':
        return '#F59E0B' // Amber color for STP
      default:
        return '#9CA3AF'
    }
  }

  // Order type tabs configuration - Lumpsum first, Redeem second, SIP hidden
  const orderTypeTabs = [
    { label: 'SIP', content: null, show: true },
    { label: 'Lumpsum', content: null, show: true },
    { label: 'Redeem', content: null, show: true },
    { label: 'Switch', content: null, show: true },
    { label: 'STP', content: null, show: true },
    { label: 'SWP', content: null, show: true }
  ]

  // Client-side filtering for mock data
  const filteredOrders = useMemo(() => {
    if (!mockOrdersData || mockOrdersData.length === 0) return [];

    if (!showTabs) return mockOrdersData;

    // Map selected index to orderType
    const typeMap = {
      0: 'SIP',
      1: 'Lumpsum',
      2: 'Redemption',
      3: 'Switch',
      4: 'STP',
      5: 'SWP'
    };

    const targetType = typeMap[selectedType];

    // Some mock data has 'SIP instalment' or similar, so we check if it includes
    return mockOrdersData.filter(order => {
      const orderType = order.orderType || '';
      return orderType.toLowerCase().includes(targetType.toLowerCase());
    });
  }, [mockOrdersData, selectedType, showTabs]);

  const handleOrderTypeTabChange = (newValue) => {
    setSelectedType(newValue)
    const type = newValue === 0 ? 'sip' : (newValue === 1 ? 'lumpsum' : (newValue === 2 ? 'redeem' : (newValue === 3 ? 'switch' : (newValue === 4 ? 'stp' : 'swp'))))
    navigate(`?type=${type}`, { replace: true })

    // Clear date filters when changing tabs
    setDateRangeValue('')
    setHasDateRange(false)

    // Notify parent about order type change for API filtering
    onOrderTypeChange(newValue)

    // Notify parent about date range clearing so API refreshes
    onDateRangeChange({ startDate: null, endDate: null })
  }

  const handleDateRangeChange = (event) => {
    setDateRangeValue(event.target.value)
    // If value is cleared, reset hasDateRange
    if (!event.target.value) {
      setHasDateRange(false)
    }
  }

  const handleDateRangeChangeCallback = ({ startDate, endDate }) => {
    // Track if date range is selected
    setHasDateRange(Boolean(startDate && endDate))
    // Pass date range to parent for API filtering
    onDateRangeChange({ startDate, endDate })
  }

  // Pagination handlers
  const handlePageChange = (page) => {
    onPageChange(page)
  }

  const handleFirstPage = () => {
    if (pagination && pagination.currentPage > 1) {
      onPageChange(1)
    }
  }

  const handlePreviousPage = () => {
    if (pagination && pagination.currentPage > 1) {
      onPageChange(pagination.currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (pagination && pagination.currentPage < pagination.totalPages) {
      onPageChange(pagination.currentPage + 1)
    }
  }

  const handleLastPage = () => {
    if (pagination && pagination.currentPage < pagination.totalPages) {
      onPageChange(pagination.totalPages)
    }
  }

  const formatMobileRangeDisplay = useCallback(({ baseValue, startDate, endDate, hasRange }) => {
    // If no range is selected
    if (!hasRange || !startDate || !endDate) {
      // On mobile, return empty string to show only icon
      // On web, return "Select Date Range" text
      return 'Select Date Range'
    }

    // Format range for mobile
    if (isMobile) {
      const formatWithoutYear = (date) =>
        date.toLocaleDateString('en-US', { day: '2-digit', month: 'short' })
      return `${formatWithoutYear(startDate)} - ${formatWithoutYear(endDate)}`
    }

    // For desktop, return the formatted range
    return baseValue
  }, [isMobile])

  const OrderLogo = ({ logo, name }) => {
    const [logoError, setLogoError] = useState(false);
    return (
      <AppBoxComponent
        sx={{
          width: { xs: theme.spacing(4), md: theme.spacing(5) },
          height: { xs: theme.spacing(4), md: theme.spacing(5) },
          borderRadius: "50%",
          background: (logo && !logoError) ? "transparent" : "linear-gradient(135deg, #46A8DC, #72B7E3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        {logo && !logoError ? (
          <img
            src={logo}
            alt={name || "Fund logo"}
            onError={() => setLogoError(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              padding: "4px",
            }}
          />
        ) : (
          <AppTypographyComponent
            sx={{
              color: "#FFFFFF",
              fontSize: { xs: "12px", md: "14px" },
              fontWeight: typography.fontWeight.bold,
            }}
          >
            {name?.charAt(0).toUpperCase() || 'F'}
          </AppTypographyComponent>
        )}
      </AppBoxComponent>
    );
  };

  const RenderOrderCard = ({ order, index, sx = {} }) => (
    <AppBoxComponent
      key={order.id || index}
      onClick={() => handleOrderClick(order.id || index)}
      sx={{
        backgroundColor: isMobile
          ? theme.palette.custom.background.page
          : alpha(theme.palette.custom.background.card, 0.9),
        borderRadius: theme.spacing(isMobile ? 1 : 1.5),
        border: `1px solid ${isMobile ? alpha(theme.palette.common.white, 0.08) : theme.palette.custom.stroke.divider}`,
        p: { xs: theme.spacing(1.5), md: theme.spacing(2) },
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: isMobile
            ? alpha(theme.palette.primary.main, 0.05)
            : alpha(theme.palette.common.white, 0.02),
          borderColor: isMobile
            ? alpha(theme.palette.primary.main, 0.3)
            : theme.palette.custom.stroke.divider,
        },
        ...sx
      }}
    >
      <AppBoxComponent sx={{ display: 'flex', gap: theme.spacing(1.5), alignItems: 'center', flex: 1, minWidth: 0 }}>
        <OrderLogo logo={order.logo} name={order.fundName} />
        {/* Left Section - Fund Details */}
        <AppBoxComponent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(0.5),
            flex: 1,
            minWidth: 0,
            pr: theme.spacing(1),
          }}
        >
          {/* Fund Name */}
          <AppTypographyComponent
            sx={{
              color: theme.palette.custom?.text?.heading || theme.palette.common.white,
              fontSize: { xs: typography.fontSize.label, md: typography.fontSize.paragraph },
              fontWeight: typography.fontWeight.semibold,
              fontFamily: typography.fontFamily,
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {order.fundName || 'N/A'}
          </AppTypographyComponent>

          <AppTypographyComponent
            sx={{
              color: theme.palette.custom?.text?.helperEnabled || theme.palette.grey[400],
              fontSize: { xs: typography.fontSize.helperText, md: typography.fontSize.label },
              fontFamily: typography.fontFamily,
              fontWeight: typography.fontWeight.regular,
            }}
          >
            {order.formattedAmount || `₹${order.amount?.toLocaleString() || '0'}`}
          </AppTypographyComponent>
        </AppBoxComponent>
      </AppBoxComponent>

      {/* Right Section - Status and Arrow */}
      <AppBoxComponent
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'flex-end', md: 'flex-start' },
          gap: { xs: 0, md: theme.spacing(1) },
          flexShrink: 0,
          alignSelf: { xs: 'flex-start', md: 'center' },
          minWidth: { xs: 120, md: 'auto' },
        }}
      >
        {/* Status with Dot */}
        <AppBoxComponent
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.75),
          }}
        >
          <AppBoxComponent
            sx={{
              width: theme.spacing(1),
              height: theme.spacing(1),
              borderRadius: '50%',
              backgroundColor: getStatusColor(order.status || 'Pending'),
              flexShrink: 0,
            }}
          />
          <AppTypographyComponent
            sx={{
              color: getStatusColor(order.status),
              fontSize: { xs: typography.fontSize.helperText, md: typography.fontSize.label },
              fontWeight: typography.fontWeight.semibold,
              fontFamily: typography.fontFamily,
              whiteSpace: 'nowrap',
            }}
          >
            {getStatusText(order.status)}
          </AppTypographyComponent>
        </AppBoxComponent>

        {/* Arrow Icon - Removed as per request */}
        {/* <KeyboardArrowRight
          sx={{
            color: theme.palette.custom?.text?.helperEnabled || theme.palette.grey[400],
            fontSize: { xs: '20px', md: '24px' },
            flexShrink: 0,
          }}
        /> */}
      </AppBoxComponent>
    </AppBoxComponent>
  );

  return (
    <AppBoxComponent sx={{
      backgroundColor: 'transparent',
      minHeight: 'auto',
      p: { xs: 0, sm: 0, md: 0 }
    }}>
      {/* Header with Tabs and Date Range */}
      <AppBoxComponent sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: { xs: 2, md: 3 },
        flexDirection: 'row',
        gap: { xs: 1, sm: 2 },
        width: '100%'
      }}>
        {showTabs ? (
          <AppBoxComponent sx={{ width: { xs: '70%', sm: 'auto' }, flex: { sm: 1 }, minWidth: 0 }}>
            <CustomTabs
              tabs={orderTypeTabs}
              variant="buttons"
              defaultTab={selectedType}
              onTabChange={handleOrderTypeTabChange}
              tabsProps={{
                sx: {
                  backgroundColor: 'transparent',
                  padding: 0,
                  marginBottom: 0,
                  gap: theme.spacing(1),
                  minHeight: 'auto',
                  width: '100%',
                  overflowX: 'auto',
                  flexWrap: 'nowrap',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                  '& .MuiTabs-scrollButtons': {
                    color: 'white'
                  }
                }
              }}
              buttonProps={{
                sx: ({ isActive }) => {
                  const primaryColor = theme.palette.custom?.button?.primary || '#0ea5e9'
                  const darkBlueBg = alpha(primaryColor, 0.15) // Dark blue background for active

                  return {
                    flex: 'none',
                    minWidth: 'auto',
                    px: { xs: theme.spacing(2), sm: theme.spacing(2.5) },
                    py: { xs: theme.spacing(0.75), sm: theme.spacing(1) },
                    borderRadius: theme.spacing(3),
                    fontSize: { xs: typography.fontSize.helperText, sm: typography.fontSize.label },
                    fontWeight: typography.fontWeight.medium,
                    textTransform: 'none',
                    boxShadow: 'none',
                    whiteSpace: 'nowrap',
                    backgroundColor: isActive
                      ? darkBlueBg
                      : 'transparent',
                    color: isActive
                      ? primaryColor
                      : theme.palette.common.white,
                    border: isActive
                      ? `1px solid ${primaryColor}`
                      : `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                    '&:hover': {
                      backgroundColor: isActive
                        ? darkBlueBg
                        : alpha(theme.palette.common.white, 0.05),
                      borderColor: isActive
                        ? primaryColor
                        : alpha(theme.palette.common.white, 0.3),
                    },
                  }
                }
              }}
            />
          </AppBoxComponent>
        ) : leftControl}

        {showDateRange && (
          <AppBoxComponent sx={{
            display: 'flex',
            alignItems: 'center',
            width: { xs: 'auto', sm: 'auto' },
            flex: { xs: showTabs ? 'none' : 1, sm: 'none' },
            flexShrink: 0,
            justifyContent: 'flex-end',
          }}>
            <DateRangePicker
              value={dateRangeValue}
              onChange={handleDateRangeChange}
              onRangeChange={handleDateRangeChangeCallback}
              startIcon={<CalendarMonth />}
              border="none"
              displayValueFormatter={formatMobileRangeDisplay}
              selectSx={{
                height: { xs: theme.spacing(5.5), sm: theme.spacing(5) },
                minHeight: { xs: theme.spacing(5.5), sm: theme.spacing(5) },
                minWidth: 0,
                width: '100%',
                maxWidth: '100%',
                px: {
                  xs: theme.spacing(1.5),
                  md: theme.spacing(2)
                },
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: { xs: "12px", md: typography.fontSize.helperText },
                backgroundColor: alpha(theme.palette.common.white, 0.05),
                borderRadius: theme.spacing(1),
                '& fieldset': {
                  border: 'none',
                },
              }}
            />
          </AppBoxComponent>
        )}
      </AppBoxComponent>

      {/* Orders Cards */}
      {filteredOrders.length > 0 ? (
        <AppBoxComponent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            // gap removed to control spacing manually
          }}
        >
          {filteredOrders.map((order, index) => {
            const showHeader = index === 0 || order.dateGroup !== filteredOrders[index - 1]?.dateGroup;

            if (showHeader && order.dateGroup) {
              return (
                <AppBoxComponent
                  key={order.id || index}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: theme.spacing(0.5), md: theme.spacing(1) },
                    mt: index === 0 ? 0 : { xs: theme.spacing(2), md: theme.spacing(3) }
                  }}
                >
                  <AppTypographyComponent
                    sx={{
                      color: theme.palette.custom?.text?.heading || theme.palette.text.primary,
                      fontSize: { xs: typography.fontSize.label, md: typography.fontSize.paragraph },
                      fontWeight: typography.fontWeight.semibold,
                    }}
                  >
                    {order.dateGroup}
                  </AppTypographyComponent>
                  <RenderOrderCard order={order} index={index} />
                </AppBoxComponent>
              );
            }

            return (
              <RenderOrderCard
                key={order.id || index}
                order={order}
                index={index}
                sx={{
                  mt: { xs: theme.spacing(1.5), md: theme.spacing(2) }
                }}
              />
            );
          })}
        </AppBoxComponent>
      ) : (
        <AppBoxComponent        >
          <NodisplayComponent
            title="No Orders found"
            subtitle="You haven't made any investments yet. Start today to build your portfolio and track your growth here."
          />
        </AppBoxComponent>
      )}

      {/* Pagination */}
      {pagination && pagination.totalRecords > 0 && (
        <AppBoxComponent
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
            borderTop: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: theme.spacing(2), sm: 0 },
            mt: theme.spacing(3),
          }}
        >
          {/* Left side - Row count */}
          <AppTypographyComponent sx={{
            color: theme.palette.custom?.text?.helperEnabled || theme.palette.grey[400],
            fontSize: { xs: typography.fontSize.helperText, sm: typography.fontSize.label }
          }}>
            {((pagination.currentPage - 1) * pagination.limit) + 1}-{Math.min(pagination.currentPage * pagination.limit, pagination.totalRecords)} of {pagination.totalRecords} rows
          </AppTypographyComponent>

          {/* Right side - Navigation buttons */}
          <AppBoxComponent
            sx={{ display: "flex", alignItems: "center", gap: { xs: theme.spacing(1), sm: theme.spacing(2) }, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-end' } }}
          >
            {/* Navigation buttons */}
            <AppBoxComponent sx={{ display: "flex", gap: theme.spacing(0.5) }}>
              <AppButtonComponent
                size="small"
                onClick={handleFirstPage}
                disabled={!pagination.hasPreviousPage}
                sx={{
                  color: theme.palette.custom?.text?.helperEnabled || theme.palette.grey[400],
                  minWidth: "auto",
                  borderRadius: theme.spacing(0.5),
                  '&:disabled': {
                    color: theme.palette.custom?.text?.helperDisabled || theme.palette.grey[600],
                  }
                }}
              >
                «
              </AppButtonComponent>
              <AppButtonComponent
                size="small"
                onClick={handlePreviousPage}
                disabled={!pagination.hasPreviousPage}
                sx={{
                  color: theme.palette.custom?.text?.helperEnabled || theme.palette.grey[400],
                  minWidth: "auto",
                  borderRadius: theme.spacing(0.5),
                  '&:disabled': {
                    color: theme.palette.custom?.text?.helperDisabled || theme.palette.grey[600],
                  }
                }}
              >
                ‹
              </AppButtonComponent>
              {/* Page number buttons - show current and adjacent pages */}
              {(() => {
                const totalPages = pagination.totalPages
                const currentPage = pagination.currentPage
                const pagesToShow = Math.min(5, totalPages)
                const pages = []

                if (totalPages <= 5) {
                  for (let i = 1; i <= totalPages; i++) {
                    pages.push(i)
                  }
                } else if (currentPage <= 3) {
                  for (let i = 1; i <= 5; i++) {
                    pages.push(i)
                  }
                } else if (currentPage >= totalPages - 2) {
                  for (let i = totalPages - 4; i <= totalPages; i++) {
                    pages.push(i)
                  }
                } else {
                  for (let i = currentPage - 2; i <= currentPage + 2; i++) {
                    pages.push(i)
                  }
                }

                return pages.map((pageNum) => (
                  <AppButtonComponent
                    key={pageNum}
                    size="small"
                    onClick={() => handlePageChange(pageNum)}
                    sx={{
                      backgroundColor: currentPage === pageNum ? (theme.palette.custom?.button?.primary || theme.palette.primary.main) : 'transparent',
                      color: currentPage === pageNum ? theme.palette.common.white : (theme.palette.custom?.text?.helperEnabled || theme.palette.grey[400]),
                      minWidth: "auto",
                      borderRadius: theme.spacing(0.5),
                      "&:hover": {
                        backgroundColor: currentPage === pageNum ? (theme.palette.custom?.button?.primary || theme.palette.primary.main) : alpha(theme.palette.common.white, 0.05),
                      },
                    }}
                  >
                    {pageNum}
                  </AppButtonComponent>
                ))
              })()}
              <AppButtonComponent
                size="small"
                onClick={handleNextPage}
                disabled={!pagination.hasNextPage}
                sx={{
                  color: theme.palette.custom?.text?.helperEnabled || theme.palette.grey[400],
                  minWidth: "auto",
                  borderRadius: theme.spacing(0.5),
                  '&:disabled': {
                    color: theme.palette.custom?.text?.helperDisabled || theme.palette.grey[600],
                  }
                }}
              >
                ›
              </AppButtonComponent>
              <AppButtonComponent
                size="small"
                onClick={handleLastPage}
                disabled={!pagination.hasNextPage}
                sx={{
                  color: theme.palette.custom?.text?.helperEnabled || theme.palette.grey[400],
                  minWidth: "auto",
                  borderRadius: theme.spacing(0.5),
                  '&:disabled': {
                    color: theme.palette.custom?.text?.helperDisabled || theme.palette.grey[600],
                  }
                }}
              >
                »
              </AppButtonComponent>
            </AppBoxComponent>
          </AppBoxComponent>
        </AppBoxComponent>
      )}
    </AppBoxComponent>
  )
}

OrdersView.propTypes = {
  mockOrdersData: PropTypes.array,
  handleOrderClick: PropTypes.func,
  pagination: PropTypes.shape({
    currentPage: PropTypes.number,
    totalPages: PropTypes.number,
    totalRecords: PropTypes.number,
    limit: PropTypes.number,
    hasNextPage: PropTypes.bool,
    hasPreviousPage: PropTypes.bool
  }),
  onPageChange: PropTypes.func,
  onDateRangeChange: PropTypes.func,
  onOrderTypeChange: PropTypes.func,
  showTabs: PropTypes.bool
}

OrdersView.defaultProps = {
  mockOrdersData: [],
  handleOrderClick: () => { },
  pagination: null,
  onPageChange: () => { },
  onDateRangeChange: () => { },
  onOrderTypeChange: () => { },
  showTabs: true
}

export default OrdersView
